import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';

// @mui
import { Alert, Box, Button, Card, Stack, TextField, Typography } from '@mui/material';

// components
import { MotionInView, varFadeInUp } from '../../../components/animate';
// utils
import { Api } from '../../../utils/api';
import { CONTACT_URLS } from '../../../utils/api/urls';

// ----------------------------------------------------------------------

// The API expects `subject` and `message` (see ContactAddSerializer); the form
// previously sent `comment` and a boolean `active`, so the message body was
// discarded on save.
async function createContact({ name, email, phone, subject, message }) {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('email', email);
  formData.append('phone', phone);
  formData.append('subject', subject);
  formData.append('message', message);

  const response = await Api.unpost(CONTACT_URLS.add, formData);
  return response;
}

// ----------------------------------------------------------------------

ConsultForm.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

export default function ConsultForm({ title = 'Contact Us', subtitle }) {
  const [status, setStatus] = useState(null);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('Name is required'),
    phone: Yup.string()
      .trim()
      .required('Phone is required')
      .min(7, 'Enter a valid phone number'),
    email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
    subject: Yup.string().trim(),
    message: Yup.string()
      .trim()
      .required('Message is required')
      .min(10, 'Please add a little more detail')
      .max(1000, 'Message is too long'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setStatus(null);

      try {
        await createContact(values);
        resetForm();
        setStatus('success');
      } catch (error) {
        console.error(error);
        setStatus('error');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting } = formik;

  return (
    <MotionInView variants={varFadeInUp}>
      <Card
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }}
      >
        <Typography component="h2" variant="h4" sx={{ color: 'text.primary', mb: subtitle ? 1 : 0.5 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {subtitle ?? 'Tell us how we can help and we will reply as soon as we can.'}
        </Typography>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={{ xs: 2.5, md: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2.5, md: 3 }}>
                <TextField
                  fullWidth
                  label="Name"
                  {...getFieldProps('name')}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  {...getFieldProps('phone')}
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2.5, md: 3 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  {...getFieldProps('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
                <TextField
                  fullWidth
                  label="Subject (optional)"
                  {...getFieldProps('subject')}
                  error={Boolean(touched.subject && errors.subject)}
                  helperText={touched.subject && errors.subject}
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                {...getFieldProps('message')}
                error={Boolean(touched.message && errors.message)}
                helperText={touched.message && errors.message}
              />
            </Stack>

            {status === 'success' && (
              <Alert severity="success" sx={{ mt: 3 }} onClose={() => setStatus(null)}>
                Thanks — your message has been sent. Our team will get back to you shortly.
              </Alert>
            )}

            {status === 'error' && (
              <Alert severity="error" sx={{ mt: 3 }} onClose={() => setStatus(null)}>
                Something went wrong while sending your message. Please try again or call us directly.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                type="submit"
                size="large"
                variant="contained"
                loading={isSubmitting}
                disabled={isSubmitting}
                sx={{ minWidth: { xs: '100%', sm: 180 } }}
              >
                Submit
              </Button>
            </Box>
          </Form>
        </FormikProvider>
      </Card>
    </MotionInView>
  );
}
